import { DEFAULT_LAUNCHER_LAYOUT } from '@accounts/shared';
import type {
  LauncherAnnouncement,
  LauncherApplication,
  LauncherLayout,
  SessionUser,
} from '@accounts/shared';
import { useQuery } from '@tanstack/react-query';
import {
  type CSSProperties,
  type ReactNode,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';

import { STR } from '../i18n';
import { listMyAnnouncements, listMyApplications } from '../lib/api';
import { brandGlyph, useBranding } from '../lib/branding';
import { formatDate, greetingFor } from '../lib/format';
import { launchAffordanceOf } from '../lib/health';
import { queryKeys } from '../lib/queryKeys';
import { SlotBrandMark } from '../ui/BrandMark';
import { Modal } from '../ui/Modal';
import { NotificationsBell } from '../ui/NotificationsBell';
import { Callout, Chip, Skeleton } from '../ui/Primitives';
import { modalButtonStyle, quietButtonStyle, rowActionStyle } from '../ui/styles';

import { AccountMenu } from './AccountMenu';
import { MyAccessRequests } from './access/MyAccessRequests';
import { RequestAccessDialog } from './access/RequestAccessDialog';

/**
 * THE LAUNCHER — where a signed-in person lands, and the only screen in this app whose whole
 * purpose is to leave it.
 *
 * The brand panel is the sign-in page's, carried across the session boundary so the product
 * reads as one thing before and after you sign in; every word of it comes from
 * `GET /api/branding` (R2), and the product's name is spelled nowhere in this file.
 *
 * The list is the server's answer to "what may THIS session enter": live grants to ACTIVE
 * applications, both filtered server-side. There is no `active` field on these rows and no
 * client-side filtering of any kind — a deactivated application is simply absent.
 *
 * TWO LAYOUTS, ONE SWITCH. `tiles` is the card grid, `list` is full-width rows; the layout
 * arrives on the SAME read as the applications (`layout`), already resolved server-side from
 * the person's stored preference or the shipped default. Nothing here re-derives it, and the
 * only place it changes is the account menu's Layout selector.
 *
 * EVERY EMPTY STATE COLLAPSES. No description means the line is absent, not blank; no tags
 * means the tag row is absent, not empty; no logo means the application's INITIAL renders in
 * the rounded square. The dashed striped placeholder is an ADMIN-side affordance and never
 * appears here.
 *
 * Announcements are the second section: LIVE ones only, in the SERVER's order — newest first
 * as it returns them, never re-sorted or re-filtered here. When there are none the region is
 * absent entirely (ruled): nothing posted is an ordinary state, not an empty box to explain.
 * A failed read is the one thing that must not masquerade as that absence, so it renders the
 * same quiet callout the applications list uses.
 */

export function LauncherPage({ user }: { user: SessionUser }) {
  const branding = useBranding();
  const data = branding.data;

  const glyph = data === undefined ? '' : brandGlyph(data);
  const tagline = data?.tagline.trim() ?? '';
  const taglineSub = data?.taglineSub.trim() ?? '';

  const applicationsQuery = useQuery({
    queryKey: queryKeys.myApplications,
    queryFn: listMyApplications,
  });
  const applications = applicationsQuery.data?.data ?? [];
  /**
   * The RESOLVED layout, straight off the read. Rendering the wrong layout while the first
   * paint is still in flight would flip the whole page on arrival, so the pre-read fallback
   * is the SHIPPED default imported from the shared contract — the same constant the server
   * resolves a null `users.launcher_layout` through. Importing it rather than repeating its
   * value is what keeps the two sides from drifting: a copy here would be a second home for
   * one fact, and the flip this fallback exists to prevent would come back the day the
   * shipped default moved server-side.
   */
  const layout: LauncherLayout = applicationsQuery.data?.layout ?? DEFAULT_LAUNCHER_LAYOUT;

  const announcementsQuery = useQuery({
    queryKey: queryKeys.myAnnouncements,
    queryFn: listMyAnnouncements,
  });
  /*
   * THE WHOLE RESPONSE, not `?.data ?? []`. `clampLines` — how many lines of a body the
   * launcher shows before "View more" — rides this same read, and the cards it governs
   * cannot render before the read resolves, so it is threaded straight from here into each
   * card. That is what lets /web hold NO copy of its default: a `?? 3` anywhere in this file
   * would be a second home for a number the server already owns, free to drift the day the
   * operator's setting or the shipped default moved.
   */
  const announcementsData = announcementsQuery.data;

  const [reading, setReading] = useState<LauncherAnnouncement | null>(null);
  /*
   * ACCESS WAVE 1 (additive). Request access opens the two-step picker; the bell sends an
   * answered request here as `?requests=open`, which scrolls My access requests into view.
   * The bell table's links (spec row 14) name the request instead — `?requestId=` or
   * `?otherRequestId=` — which also scrolls there and opens that request's History.
   */
  const [requesting, setRequesting] = useState(false);
  // Stable, so the dialog's focus effect (keyed on `onClose`) does not re-run on refetches.
  const closeRequest = useCallback(() => {
    setRequesting(false);
  }, []);
  const [searchParams] = useSearchParams();
  const focusRequestId = searchParams.get('requestId') ?? searchParams.get('otherRequestId');
  const focusRequests = searchParams.get('requests') === 'open' || focusRequestId !== null;

  function retry() {
    void applicationsQuery.refetch();
  }

  let body: ReactNode;
  if (applicationsQuery.isLoading) {
    // GENUINE first paint only. A background refetch keeps the rows already on screen —
    // skeletons flashing over real content read as a fault that did not happen.
    body = [0, 1, 2].map((row) => (
      <div className="tile tile-row" key={row}>
        <Skeleton style={{ height: 36, width: 36, borderRadius: 10 }} />
        <Skeleton style={{ height: 13, width: 150, borderRadius: 5 }} />
        <Skeleton style={{ height: 36, width: 118, borderRadius: 9, marginLeft: 'auto' }} />
      </div>
    ));
  } else if (applicationsQuery.isError) {
    /*
     * A FAILED READ AND AN EMPTY LIST MUST NOT LOOK THE SAME. Showing the warm empty state
     * here would tell somebody they have no access when the truth is that nobody asked.
     */
    body = (
      <Callout
        tone="danger"
        role="alert"
        style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}
      >
        <span style={{ flex: 1, minWidth: 180 }}>{STR.errors.couldNotLoad}</span>
        <button type="button" className="tbtn" style={quietButtonStyle} onClick={retry}>
          {STR.common.tryAgain}
        </button>
      </Callout>
    );
  } else if (applications.length === 0) {
    // A legitimate 200 with nothing in it. Nothing here may read as an error: no warning
    // tone, no alarm dot — being granted nothing yet is an ordinary state, not a fault.
    body = (
      <div className="cardx">
        <div className="nogrants">
          <div className="t">{STR.launcher.emptyTitle}</div>
          <p className="b">{STR.launcher.emptyBody}</p>
          <button type="button" className="tbtn" onClick={retry}>
            {STR.launcher.checkAgain}
          </button>
        </div>
      </div>
    );
  } else {
    body = applications.map((application) => (
      <ApplicationCard key={application.id} application={application} layout={layout} />
    ));
  }

  /*
   * The Request access entry point — the LAST item of the applications grid or list, drawn
   * in the same layout as the tiles beside it (ui-inventory: "the request-access entry point
   * on the launcher next to these tiles"; FINAL demo). Shown once the applications read has
   * answered, empty or not; never over a loading skeleton or a failed read.
   */
  const requestEntry: ReactNode =
    applicationsQuery.isLoading || applicationsQuery.isError ? null : (
      <RequestAccessEntry
        layout={layout}
        onOpen={() => {
          setRequesting(true);
        }}
      />
    );

  /*
   * The announcements region. `null` here means THE WHOLE SECTION IS ABSENT — the ruled
   * treatment for a successful read with nothing in it. Loading draws the demo's own two
   * skeleton cards; a failed read renders the same callout the applications list uses,
   * because absence is a claim ("nothing is posted") that a failure has no right to make.
   */
  let announcementsBody: ReactNode = null;
  if (announcementsQuery.isLoading) {
    announcementsBody = [0, 1].map((row) => (
      <div className="cardx ann" key={row}>
        <Skeleton style={{ height: 11, width: 170, borderRadius: 5 }} />
        <Skeleton style={{ height: 9, width: '100%', borderRadius: 5, marginTop: 9 }} />
      </div>
    ));
  } else if (announcementsQuery.isError) {
    announcementsBody = (
      <Callout
        tone="danger"
        role="alert"
        style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}
      >
        <span style={{ flex: 1, minWidth: 180 }}>{STR.errors.couldNotLoad}</span>
        <button
          type="button"
          className="tbtn"
          style={quietButtonStyle}
          onClick={() => {
            void announcementsQuery.refetch();
          }}
        >
          {STR.common.tryAgain}
        </button>
      </Callout>
    );
  } else if (announcementsData !== undefined && announcementsData.data.length > 0) {
    // The server's order IS the order — newest first, rendered exactly as returned.
    const { clampLines } = announcementsData;
    announcementsBody = announcementsData.data.map((announcement) => (
      <AnnouncementCard
        key={announcement.id}
        announcement={announcement}
        clampLines={clampLines}
        onOpen={() => {
          setReading(announcement);
        }}
      />
    ));
  }

  return (
    <main>
      <div className="c-grid">
        <aside className="c-brand">
          <div className="brand-row">
            <SlotBrandMark logoUrl={data?.logoUrl ?? null} glyph={glyph} />
            {data === undefined ? (
              <Skeleton style={{ height: 12, width: 120, borderRadius: 5 }} />
            ) : (
              <span className="brand-name">{data.name}</span>
            )}
          </div>
          {tagline === '' ? null : <p className="c-line">{tagline}</p>}
          {taglineSub === '' ? null : <p className="c-sub">{taglineSub}</p>}
          <div className="c-glyph" aria-hidden="true">
            {glyph}
          </div>
        </aside>

        <div className="c-main">
          <div className="c-top">
            <h1 className="greet-h">{greetingFor(user.name)}</h1>
            {/* The bell sits beside the account button (spec row 14). */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 'none' }}>
              <NotificationsBell user={user} placement="launcher" />
              <AccountMenu user={user} layout={layout} />
            </div>
          </div>

          <div className="sub">
            <div className="c-sec">
              <span className="mlab">{STR.launcher.sectionLabel}</span>
              <div className={layout === 'tiles' ? 'tilegrid' : 'barstack'}>
                {body}
                {requestEntry}
              </div>
            </div>
            <MyAccessRequests focus={focusRequests} openId={focusRequestId} />
            {announcementsBody === null ? null : (
              <div className="c-sec">
                <span className="mlab">{STR.launcher.announcementsSection}</span>
                <div className="annstack">{announcementsBody}</div>
              </div>
            )}
          </div>
        </div>
      </div>

      {requesting ? (
        <RequestAccessDialog onClose={closeRequest} />
      ) : null}

      {reading === null ? null : (
        <AnnouncementReader
          announcement={reading}
          onClose={() => {
            setReading(null);
          }}
        />
      )}
    </main>
  );
}

/**
 * THE LOGO SQUARE. A configured logo is painted as a background at `contain`, so it SCALES
 * TO FIT its square — never cropped, never stretched; a wide logo letterboxes against the
 * neutral surface. With no logo the application's INITIAL renders instead. Nothing here ever
 * renders as a blank box, and the admin side's dashed striped placeholder never appears.
 *
 * `logoUrl` is used VERBATIM: it arrives already cache-busted (`?v=<token>` derived from the
 * row's updated-at), so rebuilding it would restore the stale-cache bug that token exists to
 * prevent.
 */
function TileMark({ application, small }: { application: LauncherApplication; small: boolean }) {
  const initial = application.name.trim().charAt(0).toUpperCase();
  return (
    <span className={small ? 'mark mark-sm' : 'mark'} aria-hidden="true">
      {application.logoUrl === null ? (
        initial
      ) : (
        <span
          style={{
            width: '100%',
            height: '100%',
            display: 'block',
            backgroundSize: 'contain',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            backgroundImage: `url("${application.logoUrl}")`,
          }}
        />
      )}
    </span>
  );
}

/**
 * One application, and the page's primary action — drawn as a card in `tiles` and as a
 * full-width row in `list`. The two layouts differ only in arrangement: the same facts, the
 * same collapse rules, the same launch affordance.
 *
 * ACCOUNTS MINTS NOTHING PER LAUNCH. The suite session cookie is already scoped to the parent
 * domain, so the consuming application receives it on arrival and introspects. There is no
 * token, no handoff, no query parameter and no server call here: the whole mechanism is to
 * navigate to `baseUrl`, which the server validated as an absolute http(s) URL when the
 * application was registered.
 *
 * It is an ANCHOR rather than the demo's button — the correct element for a navigation, and
 * it buys middle-click, copy-link and keyboard behaviour for free.
 */
function ApplicationCard({
  application,
  layout,
}: {
  application: LauncherApplication;
  layout: LauncherLayout;
}) {
  const affordance = launchAffordanceOf(application.lastStatus);
  const version = application.lastVersion?.trim() ?? '';
  const description = application.description?.trim() ?? '';
  const stateClass =
    application.lastStatus === 'maintenance'
      ? ' t-maint'
      : application.lastStatus === 'unreachable'
        ? ' t-bad'
        : '';

  const launch = (
    <a
      className={`${affordance.buttonClass} launch`}
      href={application.baseUrl}
      aria-label={STR.launcher.launchLabel(affordance.buttonLabel, application.name)}
    >
      {affordance.buttonLabel}
    </a>
  );

  const name = (
    <>
      <span style={{ fontSize: 15, fontWeight: 650, letterSpacing: '-.01em' }}>
        {application.name}
      </span>
      {/* No version reported means no version rendered — never a dash, never "unknown". */}
      {version === '' ? null : <span className="ver">{STR.launcher.version(version)}</span>}
    </>
  );

  if (layout === 'list') {
    return (
      <section className={`tile tile-row${stateClass}`} aria-label={application.name}>
        <TileMark application={application} small />
        <div className="grow">
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
            {name}
            {affordance.chip === null ? null : (
              <Chip tone={affordance.chip.tone} style={{ fontSize: 10.5, padding: '2px 8px' }}>
                {affordance.chip.label}
              </Chip>
            )}
          </div>
          {/* NO DESCRIPTION MEANS NO LINE — nothing renders in its place. */}
          {description === '' ? null : (
            <div className="note" style={{ marginTop: 2 }}>
              {description}
            </div>
          )}
          {affordance.note === null ? null : (
            <div className="note tx-warn-strong" style={{ marginTop: 2 }}>
              {affordance.note}
            </div>
          )}
        </div>
        {/* NO TAGS MEANS NO ROW — the group collapses entirely. */}
        {application.tags.length === 0 ? null : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            {application.tags.map((tag) => (
              <span className="tag-pill" key={tag.id}>
                {tag.name}
              </span>
            ))}
          </div>
        )}
        {launch}
      </section>
    );
  }

  return (
    <section className={`tile tile-card${stateClass}`} aria-label={application.name}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <TileMark application={application} small={false} />
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 7, flexWrap: 'wrap' }}>
            {name}
          </div>
        </div>
        {affordance.chip === null ? null : (
          <Chip tone={affordance.chip.tone} style={{ fontSize: 10.5, padding: '2px 8px' }}>
            {affordance.chip.label}
          </Chip>
        )}
      </div>
      {description === '' ? null : <div className="note">{description}</div>}
      {affordance.note === null ? null : (
        <div className="note tx-warn-strong">{affordance.note}</div>
      )}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          flexWrap: 'wrap',
          marginTop: 'auto',
        }}
      >
        {application.tags.map((tag) => (
          <span className="tag-pill" key={tag.id}>
            {tag.name}
          </span>
        ))}
        <span style={{ flex: 1 }} />
        {launch}
      </div>
    </section>
  );
}

/**
 * REQUEST ACCESS, as a launcher item: a dashed card in `tiles`, a dashed row in `list` —
 * the same `.tile` boxes the applications use, so it sits in the grid as one of them and
 * never pushes an application out of its place. A button, not an anchor: it opens a dialog.
 */
function RequestAccessEntry({ layout, onOpen }: { layout: LauncherLayout; onOpen: () => void }) {
  const box: CSSProperties = {
    borderStyle: 'dashed',
    textAlign: 'left',
    color: 'var(--text)',
    background: 'var(--surface)',
    cursor: 'pointer',
    font: 'inherit',
  };
  const mark = (
    <span
      className={layout === 'list' ? 'mark mark-sm' : 'mark'}
      aria-hidden="true"
      style={{ color: 'var(--accent)' }}
    >
      {STR.access.request.entryMark}
    </span>
  );
  const action = (
    <span className="ghost launch" style={{ borderColor: 'var(--accent)', color: 'var(--accent)' }}>
      {STR.access.request.entryAction}
    </span>
  );

  if (layout === 'list') {
    return (
      <button type="button" className="tile tile-row" style={box} onClick={onOpen}>
        {mark}
        <div className="grow">
          <div style={{ fontSize: 15, fontWeight: 650, letterSpacing: '-.01em' }}>
            {STR.access.request.entryTitle}
          </div>
          <div className="note" style={{ marginTop: 2 }}>
            {STR.access.request.entryBodyList}
          </div>
        </div>
        {action}
      </button>
    );
  }

  return (
    <button type="button" className="tile tile-card" style={box} onClick={onOpen}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        {mark}
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: 15, fontWeight: 650, letterSpacing: '-.01em' }}>
            {STR.access.request.entryTitle}
          </div>
          <span className="ver">{STR.access.request.entrySub}</span>
        </div>
      </div>
      <div className="note">{STR.access.request.entryBody}</div>
      <div style={{ display: 'flex', alignItems: 'center', marginTop: 'auto' }}>
        <span style={{ flex: 1 }} />
        {action}
      </div>
    </button>
  );
}

/**
 * The pills an announcement carries IN THE READER: its application (in the operator-assigned
 * colour, or neutral when none is assigned) and its type. A legacy announcement carries
 * neither, and then the whole row is absent — never a blank pill, never the word "none".
 *
 * ONE CALLER, deliberately. The card below no longer renders pills — those two facts are its
 * rail and its kicker line now — so this is the reader modal's own row. The modal keeps the
 * pills: it is the full, unclamped announcement on a surface of its own, where a pill has
 * room to be a pill and has no rail to compete with.
 */
function AnnouncementPills({ announcement }: { announcement: LauncherAnnouncement }) {
  if (announcement.application === null && announcement.type === null) return null;
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        flexWrap: 'wrap',
        rowGap: 4,
        marginTop: 6,
      }}
    >
      {announcement.application === null ? null : (
        <Chip
          tone="neutral"
          swatch={announcement.application.color}
          style={{ fontSize: 10.5, padding: '2px 8px' }}
        >
          <span className="ptr">{announcement.application.name}</span>
        </Chip>
      )}
      {announcement.type === null ? null : (
        <Chip
          tone="neutral"
          swatch={announcement.type.color}
          style={{ fontSize: 10.5, padding: '2px 8px' }}
        >
          <span className="ptr">{announcement.type.name}</span>
        </Chip>
      )}
    </div>
  );
}

/**
 * ONE ANNOUNCEMENT, COMPOSED RATHER THAN STACKED: a type-coloured rail down the left edge,
 * the title and the date on one baseline row, a kicker naming the type (and the application
 * when the announcement is targeted), then the body, then "View more" when there is more.
 *
 * The rail and the kicker carry what a pill row used to — the pills are the reader's now.
 *
 * EVERY PART COLLAPSES, and none of them leaves a gap behind:
 *   • no title  → no `.t`, and `.d` keeps the right edge on its own;
 *   • no type   → NO RAIL, and the kicker is the application alone;
 *   • no application → the kicker is the type alone, with no trailing separator;
 *   • neither   → no kicker row at all, so the body follows the header directly.
 *
 * THE BODY IS THE SERVER'S `bodyHtml` AND NOTHING ELSE. This app has no client-side markdown
 * renderer, by ruling: the server escapes the author's text before any transform runs, so the
 * only tags in that string are the ones its own allow-list emits. `dangerouslySetInnerHTML`
 * appears here and in the author preview — for this field — and nowhere else in /web.
 */
function AnnouncementCard({
  announcement,
  clampLines,
  onOpen,
}: {
  announcement: LauncherAnnouncement;
  /** The operator's setting, resolved server-side and handed down the read. Never defaulted here. */
  clampLines: number;
  onOpen: () => void;
}) {
  const type = announcement.type;
  const application = announcement.application;

  const bodyRef = useRef<HTMLDivElement | null>(null);
  const [overflowing, setOverflowing] = useState(false);

  /*
   * "VIEW MORE" IS A MEASUREMENT, NOT A GUESS. The clamp is CSS, so the only honest way to
   * know whether anything was actually hidden is to ask the clamped element: `scrollHeight`
   * is the body's full height, `clientHeight` is what the clamp left visible. Counting the
   * source string — what this did before — was wrong in both directions: a long body of
   * short lines fits inside the clamp, and three long lines do not.
   *
   * The 1px tolerance is sub-pixel rounding, not slack. Line boxes here are fractional
   * (12.5px type at 1.55) and the two heights round independently, so a body that exactly
   * fills the clamp can report a pixel more than it shows. Genuine overflow is always at
   * least one whole line taller than that.
   */
  const measure = useCallback(() => {
    const element = bodyRef.current;
    if (element === null) return;
    setOverflowing(element.scrollHeight - element.clientHeight > 1);
  }, []);

  /*
   * Every render, and a LAYOUT effect on purpose: it settles before the browser paints, so
   * the link never flashes in and out on arrival. No dependency list, deliberately — the
   * body and the clamp count are both inputs to the measurement and both arrive as props,
   * and re-measuring is two layout reads against a list that keeps its own state in step.
   */
  useLayoutEffect(measure);

  /*
   * The input NO render announces: the width the body wraps at. A card that narrows can turn
   * a fitting body into an overflowing one without anything in React changing, so the
   * element watches its own box. Guarded because a non-browser environment (a test renderer)
   * has no ResizeObserver, and disconnected on unmount.
   */
  useLayoutEffect(() => {
    const element = bodyRef.current;
    if (element === null || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => {
      observer.disconnect();
    };
  }, [measure]);

  return (
    <div className="cardx ann">
      {/* TYPED ANNOUNCEMENTS ONLY. The swatch IS the class suffix — see `.tint-*`. */}
      {type === null ? null : <span className={`ann-rail tint-${type.color}`} aria-hidden="true" />}

      <div className="hd">
        {announcement.title === null ? null : <span className="t nm-tr">{announcement.title}</span>}
        <span className="d">{formatDate(announcement.createdAt)}</span>
      </div>

      {type === null && application === null ? null : (
        <div className="kick">
          {type === null ? null : <span className={`tint-${type.color}`}>{type.name}</span>}
          {/* The separator exists only BETWEEN the two — never trailing, never leading. */}
          {type === null || application === null ? null : (
            <span className="sep" aria-hidden="true">·</span>
          )}
          {application === null ? null : (
            <span className={application.color === null ? undefined : `tint-${application.color}`}>
              {application.name}
            </span>
          )}
        </div>
      )}

      {/*
        The clamp count travels as a custom property rather than a class per value: it is an
        operator setting with eight legal values, and eight classes would be eight places for
        the server's number to fail to arrive.
      */}
      <div
        ref={bodyRef}
        className="md md-clamp"
        style={{ '--clamp-lines': clampLines } as CSSProperties}
        dangerouslySetInnerHTML={{ __html: announcement.bodyHtml }}
      />

      {overflowing ? (
        <div>
          {/* The negative margin is the button's own 9px padding plus its 1px transparent
              border: it puts the LABEL on the body text's left edge while the hover surface
              keeps the padding that makes it look like a button. */}
          <button
            type="button"
            className="tbtn"
            style={{ ...rowActionStyle, marginTop: 6, marginLeft: -10 }}
            aria-label={STR.launcher.viewMoreLabel}
            onClick={onOpen}
          >
            {STR.launcher.viewMore}
          </button>
        </div>
      ) : null}
    </div>
  );
}

/** The full announcement — pills and all, unclamped. Same `bodyHtml`, same single source. */
function AnnouncementReader({
  announcement,
  onClose,
}: {
  announcement: LauncherAnnouncement;
  onClose: () => void;
}) {
  return (
    <Modal
      title={announcement.title ?? STR.launcher.announcementDialog}
      onClose={onClose}
      footer={
        <button type="button" className="prim" style={modalButtonStyle} onClick={onClose}>
          {STR.common.close}
        </button>
      }
    >
      <div style={{ padding: '8px 22px 0' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
          <span style={{ flex: 1, minWidth: 0 }} />
          <span className="tx-subtle" style={{ fontSize: 11.5, whiteSpace: 'nowrap' }}>
            {formatDate(announcement.createdAt)}
          </span>
        </div>
        <AnnouncementPills announcement={announcement} />
      </div>
      <div style={{ padding: '12px 22px 18px' }}>
        <div
          className="md"
          style={{ fontSize: 13 }}
          dangerouslySetInnerHTML={{ __html: announcement.bodyHtml }}
        />
      </div>
    </Modal>
  );
}
